/**
 * Post-session integrity summary — flagged participants from the session
 * report payload (same data as the PDF/XLSX Integrity section).
 */
import { useEffect, useState } from 'react';
import { useApi } from '../../../hooks/useApi';
import { Card, CardContent, CardHeader, CardTitle } from '../../ui/card';
import { Badge } from '../../ui/badge';
import { ShieldAlert, ShieldCheck, Loader2 } from 'lucide-react';

export function IntegritySummaryCard({ sessionId }) {
  const { get } = useApi();
  const [integrity, setIntegrity] = useState(null);
  const [failed, setFailed] = useState(false);

  useEffect(() => {
    let alive = true;
    get(`/live/sessions/${sessionId}/report.json`)
      .then((d) => { if (alive) setIntegrity(d.integrity || { flagged: [] }); })
      .catch(() => { if (alive) setFailed(true); });
    return () => { alive = false; };
  }, [sessionId]); // eslint-disable-line react-hooks/exhaustive-deps

  if (failed) return null;

  if (!integrity) {
    return (
      <Card className="border-white/10 bg-white/[0.03] text-slate-100" data-testid="integrity-summary-loading">
        <CardContent className="py-4 flex items-center gap-2 text-sm text-slate-400">
          <Loader2 className="w-4 h-4 animate-spin" /> Building integrity summary…
        </CardContent>
      </Card>
    );
  }

  const flagged = integrity.flagged || [];

  return (
    <Card
      className={`bg-white/[0.03] text-slate-100 ${flagged.length ? 'border-red-400/30' : 'border-emerald-400/25'}`}
      data-testid="integrity-summary-card"
    >
      <CardHeader className="pb-2">
        <CardTitle className="text-sm flex items-center gap-2 text-slate-100">
          {flagged.length ? (
            <ShieldAlert className="w-4 h-4 text-red-400" />
          ) : (
            <ShieldCheck className="w-4 h-4 text-emerald-400" />
          )}
          Integrity summary
          {flagged.length > 0 && (
            <Badge variant="destructive" className="text-[10px]" data-testid="integrity-flagged-count">
              {flagged.length} flagged
            </Badge>
          )}
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-1.5">
        {flagged.length === 0 ? (
          <p className="text-sm text-emerald-300/90" data-testid="integrity-clean-msg">
            No suspicious activity detected during this session.
          </p>
        ) : (
          <>
            <p className="text-xs text-slate-400">
              {flagged.length} of {integrity.total_participants} participants raised integrity
              signals. Full details are in the PDF/Excel report.
            </p>
            <div className="space-y-1 max-h-56 overflow-y-auto">
              {flagged.map((r) => (
                <div
                  key={r.participant_id}
                  className="flex flex-wrap items-center gap-x-2 gap-y-1 rounded-md px-2 py-1.5 text-sm bg-red-500/5 border border-red-400/20"
                  data-testid={`integrity-row-${r.participant_id}`}
                >
                  <span className={`truncate font-medium ${r.removed ? 'line-through opacity-60' : ''}`}>
                    {r.display_name}
                  </span>
                  {r.email && <span className="text-[11px] text-slate-400">{r.email}</span>}
                  <span className="flex flex-wrap gap-1 ml-auto">
                    {r.flags.map((f) => (
                      <Badge
                        key={f.key}
                        variant="outline"
                        className="text-[10px] border-red-400/40 text-red-300"
                        data-testid={`integrity-flag-${f.key}-${r.participant_id}`}
                      >
                        {f.label}
                      </Badge>
                    ))}
                  </span>
                </div>
              ))}
            </div>
          </>
        )}
      </CardContent>
    </Card>
  );
}
